"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="aspect-square animate-pulse bg-[var(--muted)]" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-[var(--mast-grey)]">
        Unable to load the catch at this moment.
      </div>;
  }
  if (products.length === 0) {
    return <div className="py-16 text-center text-[var(--mast-grey)] font-serif italic text-lg">
        New seasonal hauls arriving soon.
      </div>;
  }
  return <div className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
      {products.map((product, i) => <article key={product.id} data-index={i} className="group flex flex-col">
          <Link to={product.link} className="block">
            <div className="relative overflow-hidden bg-[var(--card)] aspect-[4/5] transition-transform duration-150 ease-out group-hover:scale-[1.02]">
              <div className="absolute inset-0 bg-gradient-radial from-[var(--nautical-primary)]/0 via-[var(--nautical-primary)]/0 to-transparent opacity-0 transition-opacity duration-150 group-hover:opacity-10" style={{
            background: "radial-gradient(circle at center, var(--nautical-primary) 0%, transparent 70%)"
          }} />
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="relative z-10 h-full w-full object-contain p-10" /> : <div className="flex h-full w-full items-center justify-center text-[var(--mast-grey)] font-serif italic">
                  The Miss Yvonne
                </div>}
              <div className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center bg-[var(--background)] text-[var(--midnight-void)] opacity-0 transition-all duration-150 group-hover:opacity-100">
                <ArrowUpRight className="h-4 w-4" />
              </div>
            </div>
          </Link>

          <div className="mt-6 flex flex-col gap-2 px-1">
            <Link to={product.link}>
              <h3 className="font-default text-[22px] leading-tight tracking-tight text-[var(--midnight-void)]">
                {product.title}
              </h3>
            </Link>
            {product.short_description ? <div className="text-sm text-[var(--mast-grey)] line-clamp-1 [&_p]:mb-0" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} /> : <p className="text-sm text-[var(--mast-grey)]">
                Wild-caught, expertly cut.
              </p>}

            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="text-xs uppercase tracking-[0.25em] text-[var(--mast-grey)] font-mono">
                From
              </span>
              <span className="font-serif text-xl text-[var(--nautical-primary)]">
                ${product.price?.toFixed(0) ?? "—"}
              </span>
            </div>

            <div className="mt-4">
              <AddToCartButton productId={product.id} variant="outline" size="sm" text="Add to cart" showQuantity={false} className="w-full" buttonClassName="bg-[var(--secondary)] text-white rounded-none font-mono uppercase tracking-widest" />
            </div>
          </div>
        </article>)}
    </div>;
}
export default function CaptainSelection() {
  const wp_query_params = {
    per_page: 4,
    orderby: "date",
    order: "desc" as const
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="2736" id="the-captains-selection" className="bg-[var(--background)] text-[var(--midnight-void)]">
      <div className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:py-48">
        {}
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <p className="mb-8 text-xs font-medium uppercase tracking-[0.25em] text-[var(--mast-grey)] font-mono">
              The Catch · 01
            </p>
            <h2 className="font-default text-4xl leading-[0.95] tracking-tighter text-[var(--midnight-void)] md:text-5xl lg:text-6xl">
              Built by Hand.{" "}
              <em className="italic text-[var(--nautical-primary)] font-normal">
                Proven by Time.
              </em>
            </h2>
          </div>

          <div className="flex items-end lg:col-span-5">
            <p className="font-default text-base leading-relaxed text-[var(--mast-grey)] md:text-lg">
              From the deck of the Miss Yvonne to your kitchen table, we deliver 
              the finest wild-caught seafood, harvested with respect for the 
              Gulf and the traditions of our trade.
            </p>
          </div>
        </div>

        {}
        <div className="mt-20 md:mt-24">
          <EcommerceProductsProvider wp_query={wp_query}>
            <ProductsGridConsumer />
          </EcommerceProductsProvider>
        </div>

        {}
        <div className="my-20 flex items-center gap-6 md:my-24">
          <Separator className="flex-1 bg-[var(--paper-border)]" />
          <span className="font-serif italic text-base text-[var(--mast-grey)] tracking-wide">
            30° 24' 42" N / 88° 49' 42" W
          </span>
          <Separator className="flex-1 bg-[var(--paper-border)]" />
        </div>

        {}
        <div className="flex justify-center">
          <Link to="/ShopPage" className="group inline-flex items-center gap-2 font-mono uppercase tracking-widest text-lg text-[var(--midnight-void)] underline decoration-[var(--paper-border)] decoration-1 underline-offset-[6px] transition-colors hover:decoration-[var(--midnight-void)]">
            View the full catalog
            <span className="transition-transform duration-150 group-hover:translate-x-1">
              →
            </span>
          </Link>
        </div>
      </div>
    </section>;
}